import { Component, Inject, OnInit } from '@angular/core';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import {MatSelectModule} from '@angular/material/select';
import {MatInputModule} from '@angular/material/input';
import {MatFormFieldModule} from '@angular/material/form-field';
import { MotivosCierreService } from '../../../../services/motivos-cierre/motivos-cierre.service';
import { CierreCotizacionService } from '../../../../services/cierre-cotizacion/cierre-cotizacion.service';
import { VentaGanadaService, VentaGanadaPostRequest, ProgramaColectivoData } from '../../../../services/venta-ganada/venta-ganada.service';

export interface MotivoCierre {
  id: number;
  descripcion: string;
}

export interface DialogCerrarData {
  title?: string;
  ventaGanadaLabel?: string;
  idTipoCotizacion?: number; // 1 = Individual, 2 = Colectivo Estándar
  idCotizacion?: number;
  usuarioCierre?: string;
}

export interface ProgramaColectivo {
  programa: string;
  lineaNegocio: string;
}

// UI model for individual venta ganada (populated from GET /venta-ganada)
export interface ProgramaVentaGanada {
  idBenPrograma: number;
  programa: string;
  nombrePrograma: string;
}

export interface LineaNegocioVentaGanada {
  lineaNegocio: string;
  programas: ProgramaVentaGanada[];
  programaSeleccionadoId: number | null;
}

export interface BeneficiarioVentaGanada {
  idBeneficiario: number;
  nombreCompleto: string;
  lineasNegocio: LineaNegocioVentaGanada[];
}

@Component({
  selector: 'app-dialog-cerrar-cotizacion',
  templateUrl: './dialog-cerrar-cotizacion.component.html',
  styleUrls: ['./dialog-cerrar-cotizacion.component.css']
})
export class DialogCerrarCotizacionComponent implements OnInit {
  selectedValue: string | null = null;
  motivoCierre: MotivoCierre | null = null;
  motivoTouched = false;
  motivosIndividuales: MotivoCierre[] = [];
  motivosColectivos: MotivoCierre[] = [];
  motivosCierre: MotivoCierre[] = [];
  observaciones: string = '';
  observacionesError = false;
  tipoContrato: 'individual' | 'colectivo' = 'individual';
  idCotizacion: number = 0;
  usuarioCierre: string = '';
  isLoading = false;

  // Variables para cotizaciones individuales (datos cargados desde GET /venta-ganada)
  beneficiariosVentaGanada: BeneficiarioVentaGanada[] = [];
  isLoadingVentaGanada = false;
  errorVentaGanada = false;

  // Variables para cotizaciones colectivas (datos cargados desde GET /venta-ganada)
  subsidioColectivo: string = '';
  programasColectivoData: ProgramaColectivoData[] = [];

  constructor(
    public dialogRef: MatDialogRef<DialogCerrarCotizacionComponent>,
    @Inject(MAT_DIALOG_DATA) public data: DialogCerrarData,
    private motivosCierreService: MotivosCierreService,
    private cierreCotizacionService: CierreCotizacionService,
    private ventaGanadaService: VentaGanadaService
  ) {
    this.tipoContrato = data.idTipoCotizacion === 2 ? 'colectivo' : 'individual';
    this.idCotizacion = data.idCotizacion || 0;
    this.usuarioCierre = data.usuarioCierre || '';
  }

  ngOnInit(): void {
    this.motivosCierreService.getMotivosCierre().subscribe({
      next: response => {
        if (response?.status && response.data) {
          this.motivosIndividuales = response.data.motivoCierreIndividual.map(
            item => ({ id: item.id, descripcion: item.valor })
          );
          this.motivosColectivos = response.data.motivoCierreColectivo.map(
            item => ({ id: item.id, descripcion: item.valor })
          );
        }
        this.motivosCierre = this.tipoContrato === 'colectivo'
          ? this.motivosColectivos
          : this.motivosIndividuales;
      },
      error: err => {
        console.error('Error al cargar motivos de cierre:', err);
      }
    });
  }

  private registrarCierreNoExitoso(): void {
    this.isLoading = true;
    this.cierreCotizacionService
      .registrarCierre(this.idCotizacion, {
        usuarioCierre: this.usuarioCierre,
        idMotivoCierre: this.motivoCierre!.id,
        observaciones: this.observaciones || null,
        esExitosa: 'N'
      })
      .subscribe({
        next: response => {
          this.isLoading = false;
          this.dialogRef.close({
            ventaGanada: 'NO',
            motivoCierre: this.motivoCierre,
            observaciones: this.observaciones,
            cierreData: response.data,
            success: response.success,
            errorMessage: response.success ? null : (response.message || 'Error al cerrar la cotización.')
          });
        },
        error: err => {
          this.isLoading = false;
          this.dialogRef.close({
            ventaGanada: 'NO',
            motivoCierre: this.motivoCierre,
            observaciones: this.observaciones,
            cierreData: null,
            success: false,
            errorMessage: err?.error?.message || 'Error al cerrar la cotización. Intente nuevamente.'
          });
        }
      });
  }

  onCancel(): void {
    this.dialogRef.close(null);
  }

  onConfirm(): void {
    if (!this.selectedValue || this.isLoading) return;
    if (this.selectedValue === 'NO' && (!this.motivoCierre || this.observacionesError)) return;

    if (this.selectedValue === 'NO') {
      this.registrarCierreNoExitoso();
      return;
    }

    if (this.selectedValue === 'SI') {
      if (this.tipoContrato === 'individual' && !this.isRadicacionValid()) return;
      this.enviarARadicacion();
      return;
    }
  }

  onRadioChange(newValue: string): void {
    this.selectedValue = newValue;
    if (newValue === 'NO') {
      this.motivoTouched = false;
    }
    if (newValue === 'SI') {
      this.motivoCierre = null;
      this.observaciones = '';
      this.observacionesError = false;
      this.cargarBeneficiariosVentaGanada();
    }
  }

  cargarBeneficiariosVentaGanada(): void {
    this.isLoadingVentaGanada = true;
    this.errorVentaGanada = false;
    this.beneficiariosVentaGanada = [];
    this.subsidioColectivo = '';
    this.programasColectivoData = [];

    // recuperar el nombre del titular desde el localStorage para poder ordenar los beneficiarios correctamente
    let nombreTitular = '';
    try {
      const contratanteStr = localStorage.getItem('datosRQ002_Contratante');
      const edicionContratanteStr = localStorage.getItem('cotizacion_edicion_contratante');
      
      let c: any = null;
      if (contratanteStr) {
        c = JSON.parse(contratanteStr);
      } else if (edicionContratanteStr) {
        c = JSON.parse(edicionContratanteStr).contratante;
      }

      if (c) {
        nombreTitular = `${c.primerNombre || ''} ${c.segundoNombre || ''} ${c.primerApellido || ''} ${c.segundoApellido || ''}`
          .normalize('NFD').replace(/[\u0300-\u036f]/g, '')
          .replace(/\s+/g, ' ').trim().toUpperCase();
      }
    } catch (e) {
      console.warn('No se pudo recuperar el Titular del storage', e);
    }

    this.ventaGanadaService.getVentaGanada(this.idCotizacion).subscribe({
      next: response => {
        this.isLoadingVentaGanada = false;
        if (response?.status && response.data) {
          const data = response.data;
          this.tipoContrato = data.idTipoCotizacion === 2 ? 'colectivo' : 'individual';
          this.motivosCierre = this.tipoContrato === 'colectivo'
            ? this.motivosColectivos
            : this.motivosIndividuales;

          if (this.tipoContrato === 'individual' && data.beneficiarios) {
            const ORDEN_LINEAS_NEGOCIO = ['MI', 'SAO', 'CEM']; 

            const listaMapeada = data.beneficiarios.map(ben => {
              // mapear y ordenar las lineas de negocio
              const lineasOrdenadas = ben.lineasNegocio.map(linea => ({
                lineaNegocio: linea.lineaNegocio,
                programas: linea.programas,
                programaSeleccionadoId: linea.programas.length === 1 ? linea.programas[0].idBenPrograma : null
              }));

              lineasOrdenadas.sort((a, b) => {
                const indexA = ORDEN_LINEAS_NEGOCIO.indexOf(a.lineaNegocio) !== -1 ? ORDEN_LINEAS_NEGOCIO.indexOf(a.lineaNegocio) : 99; 
                const indexB = ORDEN_LINEAS_NEGOCIO.indexOf(b.lineaNegocio) !== -1 ? ORDEN_LINEAS_NEGOCIO.indexOf(b.lineaNegocio) : 99; 
                return indexA - indexB;
              });

              return {
                idBeneficiario: ben.idBeneficiario,
                nombreCompleto: ben.nombreCompleto,
                lineasNegocio: lineasOrdenadas
              };
            });

            // ordenamiento 
            listaMapeada.sort((a, b) => {
            
              const nombreA = (a.nombreCompleto || '').normalize('NFD').replace(/[\u0300-\u036f]/g, '').replace(/\s+/g, ' ').trim().toUpperCase();
              const nombreB = (b.nombreCompleto || '').normalize('NFD').replace(/[\u0300-\u036f]/g, '').replace(/\s+/g, ' ').trim().toUpperCase();

              const esTitularA = nombreTitular !== '' && nombreA === nombreTitular;
              const esTitularB = nombreTitular !== '' && nombreB === nombreTitular;

              // Regla 1: Si encontramos al Titular, lo empujamos al principio (índice 0)
              if (esTitularA && !esTitularB) return -1;
              if (!esTitularA && esTitularB) return 1;

              //para los otros respetamos el orden de su ID por lo tanto se pintan en orden de quien se creo primero
              const idA = a.idBeneficiario ?? 0;
              const idB = b.idBeneficiario ?? 0;
              return idA - idB; 
            });
            this.beneficiariosVentaGanada = listaMapeada;
            
          } else if (this.tipoContrato === 'colectivo' && data.colectivo) {
            this.subsidioColectivo = data.colectivo.subsidio;
            this.programasColectivoData = data.colectivo.programas;
          } else {
            this.errorVentaGanada = true;
          }
        } else {
          this.errorVentaGanada = true;
        }
      },
      error: err => {
        this.isLoadingVentaGanada = false;
        this.errorVentaGanada = true;
        console.error('Error al cargar datos de venta ganada:', err);
      }
    });
  }

  private buildVentaGanadaBody(): VentaGanadaPostRequest {
    if (this.tipoContrato === 'colectivo') {
      return { usuarioCierre: this.usuarioCierre, selecciones: [] };
    }
    const selecciones = this.beneficiariosVentaGanada.flatMap(ben =>
      ben.lineasNegocio.map(linea => ({
        idBeneficiario: ben.idBeneficiario,
        lineaNegocio: linea.lineaNegocio,
        idBenPrograma: linea.programaSeleccionadoId!
      }))
    );
    return { usuarioCierre: this.usuarioCierre, selecciones };
  }

  private enviarARadicacion(): void {
    this.isLoading = true;
    const body = this.buildVentaGanadaBody();
    this.ventaGanadaService.postVentaGanada(this.idCotizacion, body).subscribe({
      next: response => {
        this.isLoading = false;
        if (response?.status) {
          this.dialogRef.close({
            ventaGanada: 'SI',
            cierreData: response.data,
            success: true,
            message: 'La cotización fue enviada a radicación exitosamente.'
          });
        } else {
          this.dialogRef.close({
            ventaGanada: 'SI',
            cierreData: null,
            success: false,
            errorMessage: response?.message || response?.errorMessage || 'Error al enviar a radicación.'
          });
        }
      },
      error: err => {
        this.isLoading = false;
        this.dialogRef.close({
          ventaGanada: 'SI',
          cierreData: null,
          success: false,
          errorMessage: err?.error?.message || 'Error al enviar a radicación. Intente nuevamente.'
        });
      }
    });
  }

  onMotivoChange() {
    this.motivoTouched = true;
  }

  onObservacionesChange() {
    this.observacionesError = !!this.observaciones && this.observaciones.length > 500;
  }

  isRadicacionValid(): boolean {
    return this.beneficiariosVentaGanada.length > 0 &&
      this.beneficiariosVentaGanada.every(beneficiario =>
        beneficiario.lineasNegocio.every(linea => linea.programaSeleccionadoId !== null)
      );
  }

getButtonText(): string {
    if (this.selectedValue === 'SI') {
      return this.tipoContrato === 'colectivo' ? 'CERRAR RADICACIÓN' : 'ENVIAR A RADICACIÓN';
    }
    return 'CERRAR COTIZACIÓN';
  }

  isButtonDisabled(): boolean {
    if (!this.selectedValue || this.isLoading) return true;
    if (this.selectedValue === 'NO') {
      return !this.motivoCierre || this.observacionesError;
    }
    if (this.selectedValue === 'SI') {
      if (this.isLoadingVentaGanada || this.errorVentaGanada) return true;
      if (this.tipoContrato === 'individual') {
        // Si no hay beneficiarios (datos vacíos), permitir igual para no bloquear al asesor
        if (this.beneficiariosVentaGanada.length === 0) return false;
        return !this.isRadicacionValid();
      } else if (this.tipoContrato === 'colectivo') {
        return false;
      }
    }
    return true;
  }
}
